
import React, { useEffect } from 'react';
import Navbar from '@/components/navbar';
import Footer from '@/components/Footer';
import ParticleBackground from '@/components/ParticleBackground';
import Contact from '@/components/Contact';
import { Button } from '@/components/ui/button';
import { ExternalLink } from 'lucide-react';
import SEOMetaTags from '@/components/SEOMetaTags';

const Demos = () => {
  useEffect(() => {
    document.title = 'Product Demos | Synthreo';
  }, []);

  // SEO metadata
  const demosSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Product Demos | Synthreo",
    "description": "Watch live demonstrations of our AI solutions and see how they can transform your business operations.",
    "url": "https://synthreo.ai/resources/demos"
  };

  return (
    <div className="flex min-h-screen flex-col bg-synthreo-black text-white">
      <SEOMetaTags 
        title="Product Demos | Synthreo"
        description="Watch live demonstrations of our AI solutions and see how they can transform your business operations."
        url="https://synthreo.ai/resources/demos"
        canonical="https://synthreo.ai/resources/demos"
        keywords="AI demos, product demonstrations, ThreoAgents demo, workflow automation demo"
        schema={demosSchema}
      />
      <ParticleBackground />
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative py-32 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold font-gilroy mb-6 text-gradient-blue">
              Product Demos
            </h1>
            <p className="text-xl mb-8 text-gray-300 max-w-3xl mx-auto font-poppins">
              Watch live demonstrations of our AI solutions and see how they can transform your business operations.
            </p>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-16">
              {/* How Synthreo Instantly Connects to Any API Demo */}
              <div className="bg-synthreo-black-light rounded-lg overflow-hidden border border-synthreo-blue-dark/20 hover:border-synthreo-blue-dark/50 transition-all">
                <div className="aspect-video">
                  <iframe
                    src="https://www.youtube.com/embed/QdG13VnV0B0"
                    title="How Synthreo Instantly Connects to Any API with Just a URL"
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3 font-gilroy">How Synthreo Instantly Connects to Any API with Just a URL</h3>
                  <p className="text-gray-300 mb-4 font-poppins">
                    See how Synthreo's no-code platform instantly connects to any vendor API using just a documentation URL. Watch as we auto-extract endpoints, generate secure connections, and build real agent workflows — no coding or Swagger files required.
                  </p>
                  <Button 
                    variant="link" 
                    className="text-synthreo-blue-medium p-0 flex items-center gap-2"
                    onClick={() => window.open('https://youtu.be/QdG13VnV0B0', '_blank')}
                  >
                    Watch on YouTube
                    <ExternalLink size={16} />
                  </Button>
                </div>
              </div>

              {/* ConnectWise Insights Agent Demo */}
              <div className="bg-synthreo-black-light rounded-lg overflow-hidden border border-synthreo-blue-dark/20 hover:border-synthreo-blue-dark/50 transition-all">
                <div className="aspect-video">
                  <iframe
                    src="https://www.youtube.com/embed/dNpyTMVGvIo?start=7"
                    title="ConnectWise Insights Agent Demo"
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3 font-gilroy">ConnectWise Insights Agent</h3>
                  <p className="text-gray-300 mb-4 font-poppins">
                    A smarter way to explore your ConnectWise database with graphs, summaries, and instant answers.
                  </p>
                  <Button 
                    variant="link" 
                    className="text-synthreo-blue-medium p-0 flex items-center gap-2"
                    onClick={() => window.open('https://www.youtube.com/watch?v=dNpyTMVGvIo&t=7s', '_blank')}
                  >
                    Watch on YouTube
                    <ExternalLink size={16} />
                  </Button>
                </div>
              </div>

              {/* Synthreo Agent-to-Agent Demo */}
              <div className="bg-synthreo-black-light rounded-lg overflow-hidden border border-synthreo-blue-dark/20 hover:border-synthreo-blue-dark/50 transition-all lg:col-span-2 relative">
                {/* Synthreo Logo Overlay */}
                <div className="absolute top-4 right-4 z-10 bg-white/90 p-2 rounded-lg">
                  <img
                    src="/lovable-uploads/db70a69f-7b7d-496b-9618-1da342fe0191.png"
                    alt="Synthreo Logo"
                    className="h-8 w-auto"
                  />
                </div>
                <div className="aspect-video">
                  <iframe
                    src="https://www.youtube.com/embed/jl_aIQqYrYg"
                    title="Synthreo Agent-to-Agent Demo: Function Calling in Action"
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3 font-gilroy">Synthreo Agent-to-Agent Demo: Function Calling in Action</h3>
                  <p className="text-gray-300 mb-4 font-poppins">
                    This video demonstrates how Synthreo AI agents work together using function calling to complete tasks without human intervention. You will see agents passing structured data between each other to automate multi-step workflows.
                  </p>
                  <Button 
                    variant="link" 
                    className="text-synthreo-blue-medium p-0 flex items-center gap-2"
                    onClick={() => window.open('https://youtu.be/jl_aIQqYrYg', '_blank')}
                  >
                    Watch on YouTube
                    <ExternalLink size={16} />
                  </Button>
                </div>
              </div>

              {/* ThreoAI Portal Overview Demo */}
              <div className="bg-synthreo-black-light rounded-lg overflow-hidden border border-synthreo-blue-dark/20 hover:border-synthreo-blue-dark/50 transition-all lg:col-span-2">
                <div className="aspect-video">
                  <iframe
                    src="https://www.youtube.com/embed/cun53wPiMto"
                    title="ThreoAI Portal Overview: Delivering AI Agents Through a Branded Experience"
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3 font-gilroy">ThreoAI Portal Overview: Delivering AI Agents Through a Branded Experience</h3>
                  <p className="text-gray-300 mb-4 font-poppins">
                    ThreoAI is your customizable, client-facing AI portal built for IT service providers and MSPs. This video provides a concise walkthrough of how ThreoAI enables you to deploy, manage, and scale AI agents across multiple customers with ease.
                  </p>
                  <Button 
                    variant="link" 
                    className="text-synthreo-blue-medium p-0 flex items-center gap-2"
                    onClick={() => window.open('https://youtu.be/cun53wPiMto', '_blank')}
                  >
                    Watch on YouTube
                    <ExternalLink size={16} />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-synthreo-black-light/30">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold font-gilroy mb-4">Ready to See More?</h2>
            <p className="text-xl mb-8 text-gray-300 font-poppins">
              Schedule a personalized demo to see how our AI solutions can work for your specific use case.
            </p>
            <Button className="btn-glow text-white">
              Schedule Live Demo
            </Button>
          </div>
        </section>

        <Contact />
      </main>
      <Footer />
    </div>
  );
};

export default Demos;
